import notesIcon from '@raison/static/icons/apps-line.svg';
import fundsIcon from '@raison/static/icons/file-chart-line.svg';
import bondsIcon from '@raison/static/icons/file-copy-2-line.svg';
import ventureIcon from '@raison/static/icons/funds-line.svg';
import stockIcon from '@raison/static/icons/stock-line.svg';
import {BaseCurrencyCode, Prose, Resource} from '@raison/types';
import {createAction} from '@reduxjs/toolkit';
import {queryOptions, useQuery} from '@tanstack/react-query';
import {type} from 'arktype';
import BigNumber from 'bignumber.js';
import * as Array from 'effect/Array';
import {absurd} from 'effect/Function';
import {isString} from 'effect/Predicate';
import React from 'react';
import {Route, Routes, useNavigate} from 'react-router';
import {locale} from '~/config';
import {useConversionRate, usePrimaryCurrency, usePrimaryCurrencyFormat} from '~/entities/currencies';
import {
    DashboardAssetItem,
    DashboardCategory,
    DashboardCategoryLink,
    DashboardCategoryTotal,
    DashboardDualDetailLine,
} from '~/entities/dashboard-categories';
import {LoadProblemPane} from '~/entities/fallbacks';
import {
    AssetDetailEntry,
    AssetDetails,
    AssetDetailsProvider,
    AssetDetailsRoute,
    AssetIcon,
    AssetTotal,
    DashboardCategoryContext,
    DashboardCategoryScreen,
} from '~/features/dashboard-asset-pages';
import {expectEither, useHttpClient} from '~/library/network-facade';
import {Decoder} from '@raison/prelude';
import {SkeletonContextProvider} from '~/library/ui-kit';
import {bindTracedSelector, useAppDispatch} from '~/shared/store';
import {createUseResourceSuspense} from '~/shared/store/hooks';

const knownInstrumentCategories = {
    venture: {title: 'Private Equity', color: '#8A6EBA', icon: ventureIcon},
    stocks: {title: 'Stocks', color: '#E65E5E', icon: stockIcon},
    funds: {title: 'ETFs & Funds', color: '#7097FE', icon: fundsIcon},
    bonds: {title: 'Bonds', color: '#F3DA6B', icon: bondsIcon},
    notes: {title: 'Structured Notes', color: '#89C272', icon: notesIcon},
};

export const refreshInstruments = createAction('app/refreshInstruments');

export namespace InstrumentDashboardCategory {
    export const create = createInstrumentDashboardCategory;

    export type AssetType = keyof typeof knownInstrumentCategories;
    export const isAssetType = (thing: unknown): thing is AssetType =>
        isString(thing) && thing in knownInstrumentCategories;

    export interface Asset {
        id: string | number;
        icon: string | null;
        ticker: string | null;
        issuer: Prose;
        title: string;
        yield: Prose | null;

        currency: BaseCurrencyCode;
        quantity: BigNumber;
        value: BigNumber;
    }
}

function createInstrumentDashboardCategory(
    slug: InstrumentDashboardCategory.AssetType,
    selectTotal: AppSelector<Resource<DashboardCategoryTotal>>,
    selectInstruments: AppSelector<Resource<InstrumentDashboardCategory.Asset[]>>
): DashboardCategory {
    const {color, icon, title} = knownInstrumentCategories[slug];
    const context: DashboardCategoryContext = {
        color: color,
        kindPlural: Prose.make('instruments'),
        kindSingular: Prose.make('instrument'),
    };

    const useCategoryTotal = bindTracedSelector(selectTotal);
    const useCategoryTotalSuspense = createUseResourceSuspense(selectTotal);
    const useCategoryInstruments = bindTracedSelector((state: AppModel) =>
        selectInstruments(state).mapSuccess(children => [{title: null, children}])
    );

    const useInstrumentById = bindTracedSelector((state: AppModel, assetId: string) =>
        selectInstruments(state).mapSuccess(instruments => instruments.find(i => i.id == assetId))
    );

    function renderInstrumentDetails(asset: InstrumentDashboardCategory.Asset): Prose | [Prose, Prose] {
        switch (slug) {
            case 'stocks':
                return renderQuantity(asset);
            case 'funds':
                return [asset.issuer, renderQuantity(asset)];
            case 'bonds':
            case 'notes': {
                if (asset.yield) {
                    return [asset.yield, renderQuantity(asset)];
                } else {
                    return renderQuantity(asset);
                }
            }
            case 'venture':
                return Prose.make('');
        }
    }

    function renderQuantity(asset: InstrumentDashboardCategory.Asset) {
        const qty = asset.quantity.toFormat({groupSize: 3, groupSeparator: ',', decimalSeparator: '.'});
        const ticker = asset.ticker || 'FV';
        return Prose.phrase([qty, ticker]);
    }

    function useInstrumentBalance({value, currency}: InstrumentDashboardCategory.Asset) {
        const rate = useConversionRate({from: currency, to: usePrimaryCurrency()});
        const primary = usePrimaryCurrencyFormat();
        return primary.localeFormat(value.multipliedBy(rate));
    }

    const InstrumentItem = ({asset}: {asset: InstrumentDashboardCategory.Asset}) => {
        const navigate = useNavigate();
        const amount = useInstrumentBalance(asset);
        return (
            <DashboardAssetItem
                onClick={() => navigate(`./${asset.id}`)}
                icon={asset.icon}
                fallbackIcon={icon}
                title={asset.title}
                detail={renderInstrumentDetails(asset)}
                amount={amount}
            />
        );
    };
    InstrumentItem.Skeleton = DashboardAssetItem.Skeleton;

    const InstrumentCategoryPage = () => {
        const dispatch = useAppDispatch();
        const total = useCategoryTotal();
        const assets = useCategoryInstruments();

        return (
            <DashboardCategoryScreen.Awaiting
                title={title}
                Heading={DashboardCategoryScreen.Heading}
                Item={InstrumentItem}
                total={total}
                assets={assets}
                refresh={() => dispatch(refreshInstruments())}
            />
        );
    };

    function useInstrumentDetailsOptions(assetId: string | number) {
        const http = useHttpClient();
        const {queryKey, queryFn} = queryOptions({
            queryKey: ['v1/instruments/:id', assetId],
            queryFn: async ({client, queryKey}) => {
                const response = await http(`v1/instruments/${assetId}`).then(
                    ...expectEither({
                        200: Decoder.prepare(prepareInstrumentInfo)(InstrumentInfoSchema),
                        404: null,
                    })
                );
                if (response.status === 'Abort') {
                    await client.cancelQueries({queryKey});
                    throw response.reason;
                }
                if (response.status === 404) {
                    return null;
                }
                return response.json;
            },
        });
        return {queryKey, queryFn};
    }

    const InstrumentDetailsProvider: AssetDetailsProvider = props => {
        const found = useInstrumentById(props.assetId);
        const fetched = useQuery({
            ...useInstrumentDetailsOptions(props.assetId),
            select: selectInstrumentSections,
        });

        const isLoading = found.isLoading || fetched.isLoading;
        const isError = found.isError || fetched.isError;
        if (isLoading || isError) {
            return (
                <SkeletonContextProvider value={{stopped: isError}}>
                    {props.children({
                        title: title,
                        Icon: <AssetIcon.Skeleton />,
                        Totals: <AssetTotal.Skeleton withDetails />,
                        Details: <AssetDetails.Skeleton />,
                    })}
                    {isError && <LoadProblemPane reset={fetched.refetch} />}
                </SkeletonContextProvider>
            );
        }

        if (!found.data || !fetched.data) {
            return props.notFound;
        }

        const ownIcon = found.data.icon;

        return props.children({
            title: found.data.title,
            Icon: <AssetIcon src={ownIcon ?? icon} framed={!ownIcon} />,
            Totals: <InstrumentTotal asset={found.data} />,
            Details: <AssetDetails sections={fetched.data} />,
        });
    };

    type InstrumentInfo = typeof InstrumentInfoSchema.infer;

    function selectInstrumentSections(data: InstrumentInfo | null) {
        if (data == null) {
            return data;
        }
        return getDetailsSections(data).map(Array.filter(AssetDetailEntry.isNotOptional)).filter(Array.isNonEmptyArray);
    }

    function getDetailsSections(a: InstrumentInfo): AssetDetailEntry.Optional[][] {
        const t = {
            EtfName: 'ETF name',
            Ticker: 'Ticker',
            Currency: 'Currency',
            Issuer: 'Issuer',
            Isin: 'ISIN',
            BondName: 'Bond name',
            IssueDate: 'Issue date',
            MaturityDate: 'Maturity date',
            Companies: 'Companies',
            CompanyName: 'Company name',
            NoteName: 'Note name',
        };

        switch (a.type) {
            case 'note':
                return [
                    [
                        {label: t.Companies, value: a.companies?.join(', ')},
                        {label: t.NoteName, value: a.description},
                        {label: t.Ticker, value: a.ticker},
                        {label: t.Currency, value: a.currency},
                    ],
                    [
                        {label: t.Issuer, value: a.issuer},
                        {label: t.IssueDate, value: a.issueDate?.toLocaleDateString(locale)},
                        {label: t.MaturityDate, value: a.maturityDate?.toLocaleDateString(locale)},
                    ],
                    [{label: t.Isin, value: a.isin, copy: true}],
                ];
            case 'etf':
                return [
                    [
                        {label: t.EtfName, value: a.name},
                        {label: t.Ticker, value: a.ticker},
                        {label: t.Currency, value: a.currency},
                    ],
                    [{label: t.Issuer, value: a.issuer}],
                    [{label: t.Isin, value: a.isin, copy: true}],
                ];
            case 'stock':
                return [
                    [
                        {label: t.CompanyName, value: a.companyName},
                        {label: t.Ticker, value: a.ticker},
                        {label: t.Currency, value: a.currency},
                    ],
                    [{label: t.Isin, value: a.isin, copy: true}],
                ];
            case 'bond':
                return [
                    [
                        {label: t.BondName, value: a.name},
                        {label: t.Ticker, value: a.ticker},
                        {label: t.Currency, value: a.currency},
                    ],
                    [
                        {label: t.Issuer, value: a.issuer},
                        {label: t.IssueDate, value: a.issueDate?.toLocaleDateString(locale)},
                        {label: t.MaturityDate, value: a.maturityDate?.toLocaleDateString(locale)},
                    ],
                    [{label: t.Isin, value: a.isin, copy: true}],
                ];

            default:
                return absurd(a);
        }
    }

    function InstrumentTotal({asset}: {asset: InstrumentDashboardCategory.Asset}) {
        const balance = useInstrumentBalance(asset);
        return (
            <AssetTotal
                balance={balance}
                details={<DashboardDualDetailLine center detail={renderInstrumentDetails(asset)} />}
            />
        );
    }

    return {
        categorySlug: slug,
        CategoryContextProvider({children}) {
            return <DashboardCategoryContext.Provider value={context} children={children} />;
        },
        CategoryLink({to}) {
            const total = useCategoryTotal();
            return <DashboardCategoryLink to={to} icon={icon} title={title} total={total} />;
        },
        CategoryPage() {
            return (
                <Routes>
                    <Route index element={<InstrumentCategoryPage />} />
                    <Route path=':assetId' element={<AssetDetailsRoute provider={InstrumentDetailsProvider} />} />
                </Routes>
            );
        },
        useCategoryPortion() {
            const {percent} = useCategoryTotalSuspense();
            return {
                categoryColor: color,
                categoryName: Prose.make(title),
                categoryPercent: percent,
            };
        },
    };
}

// prettier-ignore
const fields = {
    isin:         type('null | string').optional(),
    ticker:       type('null | string').optional(),
    currency:     type('null | string').optional(),
    name:         type('null | string').optional(),
    description:  type('null | string').optional(),
    issuer:       type('null | string').optional(),
    issueDate:    type('null | Date').optional(),
    maturityDate: type('null | Date').optional(),
    companyName:  type('null | string').optional(),
    companies:    type('null | string[]').optional(),
};

const InstrumentInfoSchema = type.never
    .or({
        type: "'note'",
        isin: fields.isin,
        ticker: fields.ticker,
        currency: fields.currency,
        description: fields.description,
        issuer: fields.issuer,
        issueDate: fields.issueDate,
        maturityDate: fields.maturityDate,
        companies: fields.companies,
    })
    .or({
        type: "'etf'",
        isin: fields.isin,
        ticker: fields.ticker,
        currency: fields.currency,
        name: fields.name,
        issuer: fields.issuer,
    })
    .or({
        type: "'stock'",
        isin: fields.isin,
        ticker: fields.ticker,
        currency: fields.currency,
        companyName: fields.companyName,
    })
    .or({
        type: "'bond'",
        isin: fields.isin,
        ticker: fields.ticker,
        currency: fields.currency,
        name: fields.name,
        issuer: fields.issuer,
        issueDate: fields.issueDate,
        maturityDate: fields.maturityDate,
    });

const prepareInstrumentInfo = (input: any) => ({
    type: input.type,
    isin: input.isin,
    ticker: input.ticker,
    currency: input.currency,

    description: input.asset_description,
    issuer: input.issuer,
    issueDate: new Date(input.issue_date),
    maturityDate: new Date(input.maturity_date),
    name: input.asset_name,
    companyName: input.company_name,
    companies: input.companies,
});
